import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, httpRequest } from "./services/http";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const me = await httpRequest("/user", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(me?.user || me || null);
    } catch {
      // non authentifié
    }
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        await fetchMe();
      } finally {
        setInitialized(true);
      }
    })();
  }, [fetchMe]);

  const loginUser = useCallback(
    async (credentials) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.auth.login(credentials);
        const tok = res?.token;
        if (tok) {
          setToken(tok);
          localStorage.setItem("token", tok);
        }
        // Set user immediately if returned by backend
        if (res?.user) setUser(res.user);
        else if (res?.data?.user) setUser(res.data.user);
        await fetchMe();
        return { ok: true };
      } catch (e) {
        setError(e.message || "Login failed");
        return { ok: false, error: e.message };
      } finally {
        setLoading(false);
      }
    },
    [fetchMe]
  );

  const registerUser = useCallback(
    async (data) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.auth.register(data);
        const tok = res?.token;
        if (tok) {
          setToken(tok);
          localStorage.setItem("token", tok);
        }
        if (res?.user) setUser(res.user);
        else if (res?.data?.user) setUser(res.data.user);
        await fetchMe();
        return { ok: true };
      } catch (e) {
        setError(e.message || "Register failed");
        return { ok: false, error: e.message };
      } finally {
        setLoading(false);
      }
    },
    [fetchMe]
  );

  const loginWithToken = useCallback(
    async (tok) => {
      setLoading(true);
      try {
        setToken(tok);
        localStorage.setItem("token", tok);
        await fetchMe();
        return { ok: true };
      } catch (e) {
        return { ok: false, error: e.message };
      } finally {
        setLoading(false);
      }
    },
    [fetchMe]
  );

  const logoutUser = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await httpRequest("/auth/logout", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch {
      // ignorer
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
      setLoading(false);
    }
  }, [token]);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      error,
      initialized,
      isAuthenticated: !!user,
      loginUser,
      loginWithToken,
      registerUser,
      logoutUser,
    }),
    [
      user,
      token,
      loading,
      error,
      initialized,
      loginUser,
      loginWithToken,
      registerUser,
      logoutUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
